/**
 * The Inbox's state: what the watcher found going on, and the count beside
 * ARGUS.
 *
 * The count is asked for only by someone who has Argus (rule 6), wherever
 * they are in the app: something going on is worth knowing about from any
 * page, which is what the count beside ARGUS is for. Without Argus - the flag
 * off and no permission - Argus makes no request at all, which is what the
 * separability test in `docs/plans/argus.md` asks.
 */

import { getArgusAttention, getArgusInbox, markArgusEpisode, markArgusEpisodes } from '../../api/endpoints.ts';
import type { ArgusInbox } from '../../api/types.ts';
import { Atom } from '../../state/atom.ts';
import { activeSection, isRootUser, myPermissions } from '../../state/store.ts';
import { serverFlags } from '../../state/feature-flags.ts';
import { argusAttention, hasArgus } from './argus-lens.ts';
import { userMessage } from '../../api/errors.ts';

export const inbox = new Atom<ArgusInbox | null>(null);
export const inboxStatus = new Atom<'idle' | 'loading' | 'ready' | 'failed'>('idle');
export const inboxError = new Atom<string | null>(null);

/** The Inbox shown for one rule only - set by the Rules room's chip. */
export const inboxRuleFilter = new Atom<{ id: string; name: string } | null>(null);

/** A rule the Rules room should open in its drawer on arrival - set from the Inbox. */
export const rulesRoomOpenRule = new Atom<string | null>(null);

function messageFrom(error: unknown): string {
  return userMessage(error, 'Argus could not be reached.');
}

export async function loadInbox(): Promise<void> {
  if (inboxStatus.value !== 'ready') inboxStatus.set('loading');
  try {
    inbox.set(await getArgusInbox(inboxRuleFilter.value?.id ?? null));
    inboxError.set(null);
    inboxStatus.set('ready');
  } catch (error) {
    inboxError.set(messageFrom(error));
    inboxStatus.set('failed');
  }
}

/** Mark an episode, then read the Inbox and the count again. Returns an error sentence. */
export async function markEpisode(
  id: number,
  mark: 'seen' | 'dismissed' | null,
): Promise<string | null> {
  try {
    await markArgusEpisode(id, mark);
  } catch (error) {
    return messageFrom(error);
  }
  await Promise.all([loadInbox(), loadAttention()]);
  return null;
}

/** Acknowledge (or un-acknowledge) an alert's episodes at once. Returns an error sentence. */
export async function markEpisodes(ids: number[], mark: 'seen' | null): Promise<string | null> {
  if (!ids.length) return null;
  try {
    await markArgusEpisodes(ids, mark);
  } catch (error) {
    return messageFrom(error);
  }
  await Promise.all([loadInbox(), loadAttention()]);
  return null;
}

function looking(): boolean {
  return hasArgus();
}

export async function loadAttention(): Promise<void> {
  if (!looking()) {
    if (argusAttention.value.unseen) argusAttention.set({ unseen: 0, worst: null });
    return;
  }
  try {
    const answer = await getArgusAttention();
    const now = argusAttention.value;
    if (now.unseen !== answer.unseen || now.worst !== answer.worst || (now.followed ?? 0) !== (answer.followed ?? 0)) {
      argusAttention.set(answer);
    }
  } catch {
    // Unknown is not zero, but a count that cannot be read is not a count
    // either: the badge goes, and the Inbox itself says what failed.
    argusAttention.set({ unseen: 0, worst: null });
  }
}

let started = false;

/** Keep the count current: at sign-in, on entering Argus, and each minute. */
export function startAttention(): void {
  if (started) return;
  started = true;
  // Who has Argus is known only once the account has loaded.
  // Several of these change together at sign-in; ask once for all of them.
  let soon: number | undefined;
  const again = (): void => {
    window.clearTimeout(soon);
    soon = window.setTimeout(() => void loadAttention(), 250);
  };
  activeSection.subscribe(again);
  myPermissions.subscribe(again);
  isRootUser.subscribe(again);
  serverFlags.subscribe(again);
  window.setInterval(() => void loadAttention(), 60_000);
  void loadAttention();
}
